<script>
    import { Meteor } from "meteor/meteor";
    import { Tracker } from "meteor/tracker";
    import { onMount } from "svelte";
    import { InventoryCollection } from "../../api/inventory/inventory";
    import InventoryCard from "../components/InventoryCard.svelte";

    let filters = $state({
        keyword: "",
        make: "",
        model: "",
        yearMin: "",
        yearMax: "",
        priceMin: "",
        priceMax: "",
        mileageMax: "",
        transmission: "",
        condition: "",
    });

    let vehicles = $state([]);
    let ready = $state(false);
    let showFilters = $state(false);
    let hasOpenedFilters = $state(false);
    let appliedFilterSignature = $state("");
    let handle;
    let computation;

    const activeFilterCount = $derived(
        Object.values(filters).filter(
            (value) => String(value || "").trim().length > 0,
        ).length,
    );

    function formatNumber(value) {
        return Number(value).toLocaleString("en-CA");
    }

    function buildFilterSummary() {
        const parts = [];

        if (filters.make) parts.push(filters.make);
        if (filters.model) parts.push(filters.model);

        if (filters.yearMin && filters.yearMax) {
            parts.push(`between ${filters.yearMin}-${filters.yearMax}`);
        } else if (filters.yearMin) {
            parts.push(`from ${filters.yearMin} onwards`);
        } else if (filters.yearMax) {
            parts.push(`up to ${filters.yearMax}`);
        }

        if (filters.priceMin && filters.priceMax) {
            parts.push(
                `priced between $${formatNumber(filters.priceMin)}-$${formatNumber(filters.priceMax)}`,
            );
        } else if (filters.priceMin) {
            parts.push(`priced above $${formatNumber(filters.priceMin)}`);
        } else if (filters.priceMax) {
            parts.push(`priced under $${formatNumber(filters.priceMax)}`);
        }

        if (filters.mileageMax) {
            parts.push(`with less than ${formatNumber(filters.mileageMax)} km`);
        }

        if (filters.transmission) {
            parts.push(`${filters.transmission.toLowerCase()} transmission`);
        }

        if (filters.condition) {
            parts.push(`${filters.condition.toLowerCase()} condition`);
        }

        if (filters.keyword) {
            parts.push(`matching "${filters.keyword}"`);
        }

        if (!parts.length) {
            return "Looking for all available vehicles.";
        }

        return `Looking for ${parts.join(" ")}.`;
    }

    const filterSummary = $derived(buildFilterSummary());

    function buildFilterPayload() {
        return {
            keyword: filters.keyword,
            make: filters.make,
            model: filters.model,
            yearMin: Number(filters.yearMin) || undefined,
            yearMax: Number(filters.yearMax) || undefined,
            priceMin: Number(filters.priceMin) || undefined,
            priceMax: Number(filters.priceMax) || undefined,
            mileageMax: Number(filters.mileageMax) || undefined,
            transmission: filters.transmission,
            condition: filters.condition,
        };
    }

    const currentFilterSignature = $derived(
        JSON.stringify(buildFilterPayload()),
    );
    const hasPendingFilterChanges = $derived(
        currentFilterSignature !== appliedFilterSignature,
    );

    function toggleFilters() {
        showFilters = !showFilters;
        if (showFilters) {
            hasOpenedFilters = true;
        }
    }

    function subscribeInventory() {
        handle?.stop?.();
        computation?.stop?.();

        const payload = buildFilterPayload();
        appliedFilterSignature = JSON.stringify(payload);

        ready = false;
        handle = Meteor.subscribe("inventory.public", payload);
        computation = Tracker.autorun(() => {
            ready = handle.ready();
            vehicles = InventoryCollection.find(
                {},
                { sort: { created: -1 } },
            ).fetch();
        });
    }

    onMount(() => {
        subscribeInventory();

        return () => {
            computation?.stop?.();
            handle?.stop?.();
        };
    });
</script>

<section>
    <h1>Vehicle Search</h1>
    {#if showFilters}
        <div class="search-grid">
            <label>Keyword<input bind:value={filters.keyword} /></label>
            <label>Make<input bind:value={filters.make} /></label>
            <label>Model<input bind:value={filters.model} /></label>
            <label
                >Year Min<input
                    type="number"
                    bind:value={filters.yearMin}
                /></label
            >
            <label
                >Year Max<input
                    type="number"
                    bind:value={filters.yearMax}
                /></label
            >
            <label
                >Price Min<input
                    type="number"
                    bind:value={filters.priceMin}
                /></label
            >
            <label
                >Price Max<input
                    type="number"
                    bind:value={filters.priceMax}
                /></label
            >
            <label
                >Mileage Max<input
                    type="number"
                    bind:value={filters.mileageMax}
                /></label
            >
            <label
                >Transmission
                <select bind:value={filters.transmission}>
                    <option value="">Any</option>
                    <option value="Automatic">Automatic</option>
                    <option value="Manual">Manual</option>
                </select>
            </label>
            <label
                >Condition
                <select bind:value={filters.condition}>
                    <option value="">Any</option>
                    <option value="New">New</option>
                    <option value="Used">Used</option>
                </select>
            </label>
        </div>
    {/if}
    <div class="search-actions">
        <button type="button" class="secondary-btn" onclick={toggleFilters}>
            {showFilters ? "Hide Filters" : "Show Filters"} ({activeFilterCount})
        </button>
        {#if hasOpenedFilters}
            <button
                type="button"
                onclick={subscribeInventory}
                disabled={!hasPendingFilterChanges}>Apply Filters</button
            >
        {/if}
    </div>
    {#if !showFilters}
        <p class="filter-summary">{filterSummary}</p>
    {/if}

    {#if !ready}
        <p>Searching inventory...</p>
    {:else if vehicles.length === 0}
        <p>No vehicles match those filters yet.</p>
    {:else}
        <div class="inventory-grid two-wide">
            {#each vehicles as vehicle (vehicle._id)}
                <InventoryCard {vehicle} />
            {/each}
        </div>
    {/if}
</section>
